import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { APP_SHORT_NAME, COMPANY, ROLE_LABELS } from '@sarha/shared';
import { useAuth } from '../lib/auth';
import { NAV_GROUPS, NAV_ITEMS } from './navigation';
import { Badge, Spinner, useTheme } from '../components/ui';
import { IconLogout, IconMenu, IconMoon, IconSun, IconSync, IconUser, IconWifiOff, IconClose } from '../components/icons';
import { pendingCount, syncQueue } from '../lib/idb';
import { useToast } from '../components/ui';

/** شريط حالة عدم الاتصال وطابور المزامنة */
function OfflineBanner(): JSX.Element | null {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const toast = useToast();

  const refreshPending = async () => {
    try {
      setPending(await pendingCount());
    } catch {
      setPending(0);
    }
  };

  useEffect(() => {
    void refreshPending();
    const onOnline = () => {
      setOnline(true);
      void refreshPending();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const interval = setInterval(() => void refreshPending(), 15000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(interval);
    };
  }, []);

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const outcome = await syncQueue();
      await refreshPending();
      if (outcome.attempted === 0) toast.info('لا يوجد ما يُزامن');
      else
        toast.success(
          `تمت المزامنة: ${outcome.created + outcome.updated + outcome.duplicate} سجل` +
            (outcome.conflict ? ` · ${outcome.conflict} تعارض` : '') +
            (outcome.rejected ? ` · ${outcome.rejected} مرفوض (شهر مقفل)` : ''),
        );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'فشلت المزامنة');
    } finally {
      setSyncing(false);
    }
  };

  if (online && pending === 0) return null;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs font-semibold ${
        online ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200' : 'bg-slate-800 text-white'
      }`}
    >
      <span className="flex items-center gap-2">
        {online ? <IconSync width={15} height={15} /> : <IconWifiOff width={15} height={15} />}
        {online
          ? `يوجد ${pending} سجل بانتظار المزامنة`
          : `أنت بلا اتصال — الإدخال يُحفظ محلياً${pending ? ` (${pending} بانتظار المزامنة)` : ''}`}
      </span>
      {online && pending > 0 ? (
        <button type="button" className="underline decoration-dotted" onClick={() => void runSync()} disabled={syncing}>
          {syncing ? 'جارٍ المزامنة…' : 'زامن الآن'}
        </button>
      ) : null}
    </div>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const { can, user } = useAuth();
  const items = NAV_ITEMS.filter((item) => can(item.permission));

  return (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto p-4">
      <div className="flex items-center gap-3 px-2">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-700 text-lg font-bold text-white">س</div>
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-white">{APP_SHORT_NAME}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {user ? ROLE_LABELS[user.role] : 'للخدمات اللوجستية'}
          </p>
        </div>
      </div>

      {NAV_GROUPS.map((group) => {
        const groupItems = items.filter((item) => item.group === group);
        if (groupItems.length === 0) return null;
        return (
          <div key={group}>
            <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {group}
            </p>
            <ul className="space-y-1">
              {groupItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    // «السجل اليومي» لا يبقى مُضاءً داخل «طلبات السائقين» (/daily/matrix)
                    end={item.to === '/daily'}
                    onClick={onNavigate}
                    className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}
                  >
                    <item.icon width={19} height={19} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="mt-auto rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-5 text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
        <p className="font-semibold text-slate-600 dark:text-slate-300">{COMPANY.nameAr}</p>
        <p dir="ltr" className="text-right">الرقم الضريبي: {COMPANY.vatNumber}</p>
        <p dir="ltr" className="text-right">السجل التجاري: {COMPANY.crNumber}</p>
      </div>
    </nav>
  );
}

function BottomNav(): JSX.Element {
  const { can } = useAuth();
  const items = NAV_ITEMS.filter((item) => item.mobile && can(item.permission)).slice(0, 5);

  return (
    <nav className="no-print fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 lg:hidden">
      <ul className="flex items-stretch justify-around" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {items.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-1 py-2.5 text-[11px] font-semibold transition ${
                  isActive ? 'text-brand-700 dark:text-brand-300' : 'text-slate-500 dark:text-slate-400'
                }`
              }
            >
              <item.icon width={22} height={22} />
              <span className="truncate">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-full flex-col">
      <OfflineBanner />

      <div className="flex flex-1 overflow-hidden">
        {/* لوحة جانبية على الشاشات الكبيرة */}
        <aside className="no-print hidden w-64 shrink-0 border-e border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 lg:block">
          <Sidebar />
        </aside>

        {/* درج جانبي على الجوال */}
        {drawerOpen ? (
          <div className="no-print fixed inset-0 z-50 lg:hidden">
            <button
              type="button"
              aria-label="إغلاق القائمة"
              className="absolute inset-0 bg-slate-900/50"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute inset-y-0 start-0 w-72 max-w-[85%] bg-white shadow-xl dark:bg-slate-900">
              <div className="flex items-center justify-end p-2">
                <button type="button" className="btn-ghost !p-2" onClick={() => setDrawerOpen(false)}>
                  <IconClose width={18} height={18} />
                </button>
              </div>
              <Sidebar onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="no-print flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-900 sm:px-5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-ghost !p-2 lg:hidden"
                onClick={() => setDrawerOpen(true)}
                aria-label="القائمة"
              >
                <IconMenu width={20} height={20} />
              </button>
              <div className="hidden sm:block">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{user?.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {user?.email}
                  {user ? ` · ${ROLE_LABELS[user.role]}` : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="btn-ghost !p-2"
                onClick={toggle}
                aria-label={dark ? 'الوضع النهاري' : 'الوضع الليلي'}
                title={dark ? 'الوضع النهاري' : 'الوضع الليلي'}
              >
                {dark ? <IconSun width={19} height={19} /> : <IconMoon width={19} height={19} />}
              </button>
              <NavLink to="/account" className="btn-ghost !p-2" aria-label="حسابي" title="حسابي وتغيير كلمة المرور">
                <IconUser width={19} height={19} />
              </NavLink>
              <button
                type="button"
                className="btn-ghost !p-2"
                onClick={() => void logout()}
                aria-label="خروج"
                title="تسجيل الخروج"
              >
                <IconLogout width={19} height={19} />
              </button>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-3 pb-24 pt-4 sm:px-5 lg:pb-8">
            <div className="mx-auto w-full max-w-7xl">{children}</div>
          </main>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

export function FullPageLoader(): JSX.Element {
  return (
    <div className="grid h-full place-items-center">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <Spinner className="h-7 w-7" />
        <p className="text-sm font-medium">جارٍ التحميل…</p>
        <Badge tone="brand">{COMPANY.nameAr}</Badge>
      </div>
    </div>
  );
}
